import { getSubdomainFromHost, isOrganizationSubdomain } from './hostOrg.js'

export const DEFAULT_USER_ORGANIZATION = 'Individual'
export const PENDING_ACTIVATION_CODE_STATE = 'pending_activation_code'
export const PENDING_SIGNUP_PROFILE_STATE = 'pending_signup_profile'
export const COMPLETED_ONBOARDING_STATE = 'completed'

function getHostnameFromOrigin(origin) {
  try {
    return origin ? new URL(origin).hostname : null
  } catch {
    return null
  }
}

async function fetchOrganizationBySubdomain(supabaseClient, subdomain) {
  if (!isOrganizationSubdomain(subdomain)) {
    return null
  }

  const { data, error } = await supabaseClient
    .from('organizations')
    .select('id, name, subdomain, require_activation_codes')
    .eq('subdomain', subdomain)
    .maybeSingle()

  if (error || !data?.name) {
    return null
  }

  return data
}

async function fetchOrganizationByName(supabaseClient, organizationName) {
  if (!organizationName || organizationName === DEFAULT_USER_ORGANIZATION) {
    return null
  }

  const { data, error } = await supabaseClient
    .from('organizations')
    .select('id, name, subdomain, require_activation_codes')
    .eq('name', organizationName)
    .maybeSingle()

  if (error || !data?.name) {
    return null
  }

  return data
}

export async function resolveVerifiedOrganizationForUser({
  supabaseClient,
  hostname,
  targetOrigin = null,
  authUser = null,
  explicitOrganizationName = null,
}) {
  const currentHostOrganization = await fetchOrganizationBySubdomain(
    supabaseClient,
    getSubdomainFromHost(hostname)
  )
  if (currentHostOrganization) {
    return {
      organization: currentHostOrganization.name,
      source: 'current_host',
      organizationRecord: currentHostOrganization,
    }
  }

  const targetHostOrganization = await fetchOrganizationBySubdomain(
    supabaseClient,
    getSubdomainFromHost(getHostnameFromOrigin(targetOrigin))
  )
  if (targetHostOrganization) {
    return {
      organization: targetHostOrganization.name,
      source: 'target_origin',
      organizationRecord: targetHostOrganization,
    }
  }

  const explicitOrganization = await fetchOrganizationByName(supabaseClient, explicitOrganizationName)
  if (explicitOrganization) {
    return {
      organization: explicitOrganization.name,
      source: 'explicit_name',
      organizationRecord: explicitOrganization,
    }
  }

  const metadataSubdomain = authUser?.user_metadata?.signup_org_subdomain || null
  const metadataOrganizationName = authUser?.user_metadata?.signup_org_name || null
  const metadataOrganization = await fetchOrganizationBySubdomain(supabaseClient, metadataSubdomain)

  if (
    metadataOrganization &&
    (!metadataOrganizationName || metadataOrganization.name === metadataOrganizationName)
  ) {
    return {
      organization: metadataOrganization.name,
      source: 'user_metadata',
      organizationRecord: metadataOrganization,
    }
  }

  return { organization: DEFAULT_USER_ORGANIZATION, source: 'default', organizationRecord: null }
}

function shouldPromoteOrganization(existingOrganization, nextOrganization) {
  if (!nextOrganization || nextOrganization === DEFAULT_USER_ORGANIZATION) {
    return false
  }

  if (!existingOrganization) {
    return true
  }

  return existingOrganization === DEFAULT_USER_ORGANIZATION
}

export async function ensureUserAccessForAuthenticatedUser({
  supabaseClient,
  authUser,
  hostname,
  targetOrigin = null,
  explicitOrganizationName = null,
  debug = false,
}) {
  if (!authUser?.id) {
    return { organization: DEFAULT_USER_ORGANIZATION, source: 'no_user', didWrite: false, userAccess: null }
  }

  // The client sends the anon key whenever it has no live session. Without a token for
  // THIS user every query below runs as `anon`: the user_access select returns nothing
  // (RLS), so we would try to INSERT and hit an RLS violation, and the partner-claim RPC
  // would be rejected. Skip until the session actually carries this user's token.
  const { data: sessionData } = await supabaseClient.auth.getSession()
  const liveSession = sessionData?.session
  if (!liveSession?.access_token || liveSession.user?.id !== authUser.id) {
    if (debug) console.warn('[AuthOrg] Skipping user_access ensure: no live session for user yet')
    return { organization: DEFAULT_USER_ORGANIZATION, source: 'no_session', didWrite: false, userAccess: null }
  }

  const resolved = await resolveVerifiedOrganizationForUser({
    supabaseClient,
    hostname,
    targetOrigin,
    authUser,
    explicitOrganizationName,
  })

  // A partner (e.g. NM) row for this verified email means the user belongs to that org even
  // when they signed in directly, such as after an interrupted partner launch.
  if (resolved.source === 'default') {
    const { data: partnerOrganization, error: claimError } = await supabaseClient.rpc('claim_partner_membership')
    if (claimError) {
      if (debug) console.warn('[AuthOrg] claim_partner_membership failed:', claimError.message)
    } else if (partnerOrganization) {
      const { data: claimedAccess } = await supabaseClient
        .from('user_access')
        .select('id, organization, role, status, usage_plan, onboarding_state')
        .eq('id', authUser.id)
        .maybeSingle()
      return {
        organization: claimedAccess?.organization || partnerOrganization,
        source: 'partner_claim',
        didWrite: true,
        requiresActivationCode: false,
        userAccess: claimedAccess || null,
      }
    }
  }

  let { data: userAccess } = await supabaseClient
    .from('user_access')
    .select('id, organization, role, status, usage_plan, onboarding_state')
    .eq('id', authUser.id)
    .maybeSingle()

  let didWrite = false

  const requiresActivationCode = Boolean(
    resolved.organizationRecord?.require_activation_codes
      && resolved.organization
      && resolved.organization !== DEFAULT_USER_ORGANIZATION
  )
  const shouldGateNewOrgAccess = Boolean(
    requiresActivationCode
      && (
        !userAccess
        || (
          shouldPromoteOrganization(userAccess?.organization, resolved.organization)
          && (userAccess?.onboarding_state || COMPLETED_ONBOARDING_STATE) === COMPLETED_ONBOARDING_STATE
        )
      )
  )

  if (!userAccess) {
    const insertPayload = {
      id: authUser.id,
      email: authUser.email,
      role: 'learner',
      status: 'active',
      organization: resolved.organization,
      usage_plan: 'free',
      onboarding_state: shouldGateNewOrgAccess ? PENDING_ACTIVATION_CODE_STATE : COMPLETED_ONBOARDING_STATE,
    }

    const { error: insertError } = await supabaseClient
      .from('user_access')
      .insert([insertPayload])

    if (insertError && insertError.code !== '23505' && !String(insertError.message || '').toLowerCase().includes('duplicate')) {
      throw insertError
    }

    didWrite = !insertError

    const refetched = await supabaseClient
      .from('user_access')
      .select('id, organization, role, status, usage_plan, onboarding_state')
      .eq('id', authUser.id)
      .maybeSingle()

    if (refetched.error) {
      throw refetched.error
    }
    userAccess = refetched.data
  }

  if (shouldPromoteOrganization(userAccess?.organization, resolved.organization)) {
    const nextOnboardingState = (
      requiresActivationCode
      && (userAccess?.onboarding_state || COMPLETED_ONBOARDING_STATE) === COMPLETED_ONBOARDING_STATE
    )
      ? PENDING_ACTIVATION_CODE_STATE
      : (userAccess?.onboarding_state || COMPLETED_ONBOARDING_STATE)
    const { error: updateError } = await supabaseClient
      .from('user_access')
      .update({
        organization: resolved.organization,
        onboarding_state: nextOnboardingState,
      })
      .eq('id', authUser.id)

    if (updateError) {
      throw updateError
    }

    didWrite = true
    userAccess = {
      ...userAccess,
      organization: resolved.organization,
      onboarding_state: nextOnboardingState,
    }
  } else if (
    debug &&
    userAccess?.organization &&
    resolved.organization !== DEFAULT_USER_ORGANIZATION &&
    userAccess.organization !== resolved.organization
  ) {
    console.warn('[AuthOrg] Skipping org overwrite for user', authUser.id, {
      existingOrganization: userAccess.organization,
      resolvedOrganization: resolved.organization,
      source: resolved.source,
    })
  }

  return {
    organization: userAccess?.organization || resolved.organization,
    source: resolved.source,
    didWrite,
    requiresActivationCode,
    userAccess,
  }
}
